<script setup lang="ts">
    const bonus = useBonus();

    const containerRef = ref<HTMLElement | null>(null);
    let removeFallbackListeners: (() => void) | null = null;

    onMounted(() => {
        if (!import.meta.client) return;

        const supportsCalcSize = window.CSS.supports('width: calc-size(auto, size)');
        if (supportsCalcSize !== false) return;

        const container = containerRef.value;
        if (!container) return;

        const handleEnter = () => {
            if (!container.querySelector('.text')) return;
            container.style.width = `${container.scrollWidth}px`;
            container.classList.add('--hover');
        };
        const handleLeave = () => {
            if (!container.querySelector('.text')) return;
            container.style.removeProperty('width');
            container.classList.remove('--hover');
        };

        container.addEventListener('mouseenter', handleEnter);
        container.addEventListener('mouseleave', handleLeave);

        removeFallbackListeners = () => {
            container.removeEventListener('mouseenter', handleEnter);
            container.removeEventListener('mouseleave', handleLeave);
        };
    });

    onBeforeUnmount(() => {
        removeFallbackListeners?.();
    });
</script>

<template>
    <div
        v-if="bonus.isCurrentUserParticipating.value"
        ref="containerRef"
        class="container"
        :aria-busy="bonus.isLoading.value"
    >
        <SimpleLoadingSpinner
            v-if="bonus.isLoading.value"
            :size="20"
            color="currentColor"
            role="progressbar"
            :aria-label="bonus.currentUsersPoints.unit"
        />
        <Icon
            v-else
            class="icon"
            name="lucide:star"
            size="20"
            alt=""
            aria-hidden="true"
            mode="svg"
        />

        <div
            v-if="bonus.currentUsersPoints.raw.value !== null"
            class="text"
        >
            {{ bonus.currentUsersPoints.formatted.value }} {{ bonus.currentUsersPoints.unit }}
        </div>
    </div>
</template>

<style lang="css" scoped>
    .container {
        background-color: var(--brand-secondary);
        color: var(--black);
        /* Arbitrary large value for pill shape */
        border-radius: 99px;
        display: flex;
        gap: 4px;
        align-items: center;
        padding: 4px;
        position: absolute;
        right: calc(100% - 12px);
        top: -4px;
        font-size: var(--font-xxs);
        overflow: hidden;
        width: 28px;
        transition:
            width 0.3s ease,
            gap 0.3s ease;

        &.--hover:has(.text),
        &:hover:has(.text) {
            padding-right: 6px;

            @supports (width: calc-size(auto, size)) {
                width: calc-size(auto, size);
            }

            .text {
                opacity: 1;
            }
        }
    }

    .icon {
        flex: none;
        width: 20px;
        height: 20px;
    }

    .text {
        flex: none;
        opacity: 0;
        transition: opacity 0.3s ease;
        white-space: nowrap;
    }
</style>
